import { hasReposted as hasRepostedAtom, ingestEvents, repostCount } from '@/atoms/stats'
import { getErrorMessage } from '@/lib/error'
import { trimTagEnd } from '@/lib/draft-event'
import { cn, now } from '@/lib/utils'
import {
  addEventToCache,
  determineTargetRelays,
  emitEphemeralEvent,
  emitEventRemoved,
  getEventHint,
  publishEventAndDisplay,
  removeEventFromCache
} from '@/services/client.service'
import * as kinds from '@nostr/tools/kinds'
import { canWriteAtom, unwrappedSignerAtom, useCurrentPubkey } from '@/atoms/session'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading03Icon, SquareArrowReloadIcon } from '@hugeicons/core-free-icons'
import { Event } from '@nostr/tools/wasm'
import { useAtomValue } from 'jotai'
import { getDefaultStore } from 'jotai'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { formatCount } from './utils'
import { addPendingPublish, removePendingPublish, usePendingCountdown } from '@/lib/pendingPublish'
import { discardPendingEvent, savePendingEvent } from '@/atoms/pending'
import { invalidateFeed } from '@/atoms/invalidation'
import { generateBech32IdFromATag, generateBech32IdFromETag } from '@/lib/tag'
import { getReplaceableCoordinateFromEvent, isReplaceableEvent } from '@/lib/event'
import { toast } from 'sonner'

const PENDING_PUBLISH_MS = 10_000
const store = getDefaultStore()

// NIP-18: kind 6 for notes, kind 16 generic repost for everything else,
// with mandatory k and r tags plus optional a tag for replaceable events.
function buildRepostTags(event: Event): string[][] {
  const eTag = trimTagEnd(['e', event.id, getEventHint(event.id), event.pubkey])
  const tags: string[][] = [eTag, ['p', event.pubkey]]

  if (event.kind !== kinds.ShortTextNote) {
    tags.push(['k', String(event.kind)])
    if (isReplaceableEvent(event.kind)) {
      const aTag = trimTagEnd([
        'a',
        getReplaceableCoordinateFromEvent(event),
        getEventHint(event.id)
      ])
      tags.push(aTag)
      const naddr = generateBech32IdFromATag(aTag)
      if (naddr) tags.push(['r', naddr])
    } else {
      const nevent = generateBech32IdFromETag(eTag)
      if (nevent) tags.push(['r', nevent])
    }
  }

  return tags
}

function PendingPublishToast({ endAt, onUndo }: { endAt: number; onUndo: () => void }) {
  const secondsLeft = usePendingCountdown(endAt)
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-3 rounded-full bg-primary text-primary-foreground shadow hover:bg-primary-hover py-2 pl-4 pr-3">
      <div className="text-sm font-medium">{t('Changed your mind?')}</div>
      <div className="text-sm font-medium w-8 text-center">{secondsLeft}s</div>
      <Button variant="secondary" size="sm" onClick={onUndo}>
        {t('Cancel')}
      </Button>
    </div>
  )
}

function waitForAbortableDelay(duration: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const timeout = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort)
      resolve()
    }, duration)
    const handleAbort = () => {
      window.clearTimeout(timeout)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal.addEventListener('abort', handleAbort)
  })
}

export default function RepostButton({ event }: { event: Event }) {
  const { t } = useTranslation()
  const pubkey = useCurrentPubkey()
  const count = useAtomValue(repostCount(event.id))
  const has = useAtomValue(hasRepostedAtom(event.id))
  const [reposting, setReposting] = useState(false)
  const signer = useAtomValue(unwrappedSignerAtom)
  const canWrite = useAtomValue(canWriteAtom)
  const canRepost = !has && !reposting

  return (
    <button
      className={cn(
        'flex gap-1 items-center enabled:hover:text-lime-500 disabled:opacity-50 disabled:cursor-not-allowed px-3 h-full',
        has ? 'text-lime-500' : 'text-muted-foreground'
      )}
      title={t('Repost')}
      onClick={(e) => {
        e.stopPropagation()
        repost()
      }}
      disabled={!canRepost || !canWrite}
    >
      {reposting ? (
        <HugeiconsIcon icon={Loading03Icon} className="animate-spin" />
      ) : (
        <HugeiconsIcon icon={SquareArrowReloadIcon} />
      )}
      {!!count && <div className="text-sm">{formatCount(count)}</div>}
    </button>
  )

  async function repost() {
    if (!canRepost || !pubkey) return

    setReposting(true)
    const timer = setTimeout(() => setReposting(false), 5000)

    try {
      if (has) return

      if (!signer) throw new Error('no signer connected')

      const isNote = event.kind === kinds.ShortTextNote
      const repostEvent = await signer.signEvent({
        kind: isNote ? kinds.Repost : kinds.GenericRepost,
        content: event.tags.some(([t]) => t === '-') ? '' : JSON.stringify(event),
        tags: buildRepostTags(event),
        created_at: now()
      })
      if (!repostEvent) throw new Error('sign event returned null')

      const relayUrls = await determineTargetRelays(repostEvent)

      // persist immediately so closing the tab during the undo window
      // doesn't lose the event; it can be published later from /pending
      void savePendingEvent(repostEvent)

      addEventToCache(repostEvent)
      emitEphemeralEvent(repostEvent)
      store.set(hasRepostedAtom(event.id), true)

      const abortController = new AbortController()
      const toastId = `pending-repost-${repostEvent.id}`
      const endAt = Date.now() + PENDING_PUBLISH_MS
      const undoRepost = () => {
        abortController.abort()
        removeEventFromCache(repostEvent.id)
        void discardPendingEvent(repostEvent.id)
        emitEventRemoved(repostEvent.id)
        removePendingPublish(repostEvent.id)
        store.set(hasRepostedAtom(event.id), false)
        toast.dismiss(toastId)
        invalidateFeed()
      }
      addPendingPublish(repostEvent.id, endAt, undoRepost)
      toast(<PendingPublishToast endAt={endAt} onUndo={undoRepost} />, {
        id: toastId,
        duration: PENDING_PUBLISH_MS,
        className: '!bg-transparent !border-none !shadow-none !p-0',
        style: { '--width': 'fit-content' } as React.CSSProperties
      })

      try {
        await waitForAbortableDelay(PENDING_PUBLISH_MS, abortController.signal)
        toast.dismiss(toastId)
        removePendingPublish(repostEvent.id)
        await publishEventAndDisplay(relayUrls, repostEvent)
        await discardPendingEvent(repostEvent.id)
        ingestEvents([repostEvent])
        toast.success(t('Reposted successfully!'), { duration: 2000 })
      } catch (error) {
        toast.dismiss(toastId)
        removePendingPublish(repostEvent.id)
        if (abortController.signal.aborted) return
        ingestEvents([repostEvent])
        toast.error(t('Repost failed'))
        console.error('repost publish failed', error)
      }
    } catch (error) {
      console.error('repost failed', error)
      toast.error(t('Repost failed: {{error}}', { error: getErrorMessage(error) }))
    } finally {
      setReposting(false)
      clearTimeout(timer)
    }
  }
}
